import { FC, useMemo, useState } from 'react'
import { Box, CircularProgress } from '@mui/material'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import { useIntl } from 'react-intl'
import { useActiveLeaseContractId } from 'services/contracts/hooks/useActiveLeaseContractId'
import {
  BOOKING_AGREEMENT_DELIVERY_METHOD,
  BookingAgreementReceipt,
  useLeaseContractsBookingAgreement,
  useLeaseContractsBookingAgreementReceipt
} from 'services/contracts/hooks/useLeaseContractsBookingAgreement'
import AdditionalServicesBookingAgreementConsent from './AdditionalServicesBookingAgreementConsent'
import AdditionalServicesBookingAgreementLegacy from './AdditionalServicesBookingAgreementLegacy'
import AdditionalServicesBookingAgreementReceipt from './AdditionalServicesBookingAgreementReceipt'

dayjs.extend(isBetween)

const AdditionalServicesBookingAgreement: FC = () => {
  const { formatMessage } = useIntl()
  const { activeLeaseContractId } = useActiveLeaseContractId()
  const { bookingAgreementPdf: metadata, isLoading: isMetadataLoading } =
    useLeaseContractsBookingAgreement(activeLeaseContractId)

  const isAlreadyAccepted = !!metadata?.alreadyAcceptedAt
  const isAcceptanceFlowAvailable =
    !!metadata && metadata.isAcceptanceAvailable === true && !metadata.isWisContract

  const { receipt: fetchedReceipt, isLoading: isReceiptLoading } =
    useLeaseContractsBookingAgreementReceipt(activeLeaseContractId, isAlreadyAccepted)

  const [localReceipt, setLocalReceipt] = useState<BookingAgreementReceipt | null>(null)
  const [forceReceiptView, setForceReceiptView] = useState(false)

  const receipt = localReceipt ?? fetchedReceipt

  const fallbackMessage = useMemo(() => {
    if (!metadata) return undefined
    if (metadata.isWisContract) {
      return formatMessage({
        id: 'additionalServicesBookingAgreement.wisHidden',
        defaultMessage: 'Umowa rezerwacyjna w tej formie nie wymaga akceptacji w aplikacji.'
      })
    }
    if (
      metadata.deliveryMethod &&
      metadata.deliveryMethod !== BOOKING_AGREEMENT_DELIVERY_METHOD.MOBILE_APP
    ) {
      return formatMessage({
        id: 'additionalServicesBookingAgreement.deliveredOffline',
        defaultMessage:
          'Umowa zostanie podpisana w inny sposób — skontaktuj się ze swoim opiekunem.'
      })
    }
    if (metadata.isAcceptanceAvailable === false && !metadata.bookingAgreementPdf) {
      return formatMessage({
        id: 'additionalServicesBookingAgreement.notReady',
        defaultMessage: 'Umowa jeszcze nie jest gotowa, wróć później.'
      })
    }
    return undefined
  }, [formatMessage, metadata])

  if (isMetadataLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!metadata) {
    return <AdditionalServicesBookingAgreementLegacy />
  }

  const shouldRenderReceipt =
    !!receipt && (isAlreadyAccepted || forceReceiptView || !!localReceipt)

  if (shouldRenderReceipt && receipt) {
    return (
      <AdditionalServicesBookingAgreementReceipt receipt={receipt} metadata={metadata} />
    )
  }

  if (isAlreadyAccepted && isReceiptLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (isAcceptanceFlowAvailable) {
    return (
      <AdditionalServicesBookingAgreementConsent
        contractId={activeLeaseContractId}
        metadata={metadata}
        onAccepted={(receiptResult) => {
          setLocalReceipt(receiptResult)
          setForceReceiptView(true)
        }}
        onAlreadyAccepted={() => {
          setForceReceiptView(true)
        }}
      />
    )
  }

  return (
    <AdditionalServicesBookingAgreementLegacy metadata={metadata} infoMessage={fallbackMessage} />
  )
}

export default AdditionalServicesBookingAgreement
